import type { StickerLayoutSnapshot, TextLine, TextV2Data } from "../typesV2";
import { calculateLineHeight, getMaxLineWidth } from "./measurements";
import { cloneLines, deltaToLines, reflowTextLines } from "./wrap";

type BuildStickerLayoutArgs = {
    data: TextV2Data;
    sourceLines: TextLine[];
    hardWidth: number;
};

type FindBestStickerLayoutArgs = {
    data: TextV2Data;
    sourceLines: TextLine[];
    hardWidth: number;
    hardHeight: number;
    minFontSize?: number;
    maxFontSize?: number;
};

export const buildStickerLayout = ({
    data,
    sourceLines,
    hardWidth,
}: BuildStickerLayoutArgs): StickerLayoutSnapshot => {
    const metrics = calculateLineHeight(data);
    const reflow = reflowTextLines(
        cloneLines(sourceLines),
        hardWidth,
        data,
        metrics.horizontalPadding,
        metrics.lineHeight,
    );
    const width = getMaxLineWidth(reflow.lineWidths);
    const height = reflow.lines.length * metrics.lineHeight;

    return {
        fontSize: data.fontSize,
        lineHeight: metrics.lineHeight,
        verticalPadding: metrics.verticalPadding,
        horizontalPadding: metrics.horizontalPadding,
        lines: reflow.lines,
        lineWidths: reflow.lineWidths,
        width,
        height,
    };
};

export const findBestStickerLayout = ({
    data,
    sourceLines,
    hardWidth,
    hardHeight,
    minFontSize = 1,
    maxFontSize = Math.max(1, Math.floor(data.fontSize)),
}: FindBestStickerLayoutArgs): StickerLayoutSnapshot => {
    const buildAt = (fontSize: number) =>
        buildStickerLayout({
            data: {
                ...data,
                fontSize,
            },
            sourceLines,
            hardWidth,
        });

    let min = Math.max(1, minFontSize);
    let max = Math.max(min, maxFontSize);
    let current = buildAt(max);

    while (current.width <= hardWidth && current.height <= hardHeight) {
        min = max;
        max *= 2;
        current = buildAt(max);

        if (max > 512) {
            break;
        }
    }

    let low = min;
    let high = max;
    let best = min;
    let bestLayout = buildAt(best);

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const midLayout = buildAt(mid);

        if (midLayout.width <= hardWidth && midLayout.height <= hardHeight) {
            best = mid;
            bestLayout = midLayout;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return best === bestLayout.fontSize ? bestLayout : buildAt(best);
};

export const findBestStickerLayoutFromDelta = ({
    data,
    hardWidth,
    hardHeight,
    minFontSize,
    maxFontSize,
}: Omit<FindBestStickerLayoutArgs, "sourceLines">): StickerLayoutSnapshot => {
    return findBestStickerLayout({
        data,
        sourceLines: deltaToLines(data.delta),
        hardWidth,
        hardHeight,
        minFontSize,
        maxFontSize,
    });
};
